'use client';

import { Button } from '@/components/ui/button';
import { CloseButton } from '@/components/ui/buttons';
import { PATH } from '@/constants';
import { NAV_HEADER_MENUS } from '@/constants/nav';
import { useAuth } from '@/contexts/AuthProvider';
import clsx from 'clsx';
import { getImageUrl } from '@/lib/cdn';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import * as React from 'react';
import HamburgerButton from '../ProtectedLayout/HamburgerButton';
import { usePostHog } from 'posthog-js/react';
import BookDemoButton from '@/views/home/components/BookDemoButton';

const MOBILE_PRODUCT_GROUPS: { label: string; items: { name: string; href: string }[] }[] = [
  {
    label: 'Sales',
    items: [
      { name: 'Daily SDR Playbook', href: '/products/daily-playbook' },
      { name: 'Website Visitor ID', href: '/products/visitor-identification' },
      { name: 'Email Automation', href: '/products/emails' },
      { name: 'Smart Dialer', href: '/products/cold-calls' },
      { name: 'Champion Tracking', href: '/products/champion-tracking' },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { name: 'AI Chatbot', href: '/products/ai-chatbot' },
      { name: 'AI SEO', href: '/products/ai-seo' },
      { name: 'Website Visitor ID', href: '/products/visitor-identification' },
    ],
  },
];

type IMobileMenuProps = React.HTMLAttributes<HTMLDivElement>;

const MobileMenu: React.FunctionComponent<IMobileMenuProps> = (props) => {
  const posthog = usePostHog();
  const [isShowMenu, setIsShowMenu] = React.useState(false);
  const [expandedMenu, setExpandedMenu] = React.useState<number | null>(null);

  const { isAuthenticated } = useAuth();
  const pathname = usePathname() ?? '';

  const navMenusAfterProducts = NAV_HEADER_MENUS.filter((item) => item.title !== 'Home');

  return (
    <div className={clsx(props.className)}>
      <HamburgerButton onClick={() => setIsShowMenu(true)} />
      <div
        className={clsx(
          'fixed !z-[9999999] top-0 right-0 left-0 bottom-0 bg-white p-1 flex flex-col h-full animate-opacity',
          isShowMenu ? 'block' : 'hidden',
        )}
      >
        <div className='p-4 flex w-full justify-between items-center'>
          <Link href='/'>
            <Image
              className='w-40'
              src={getImageUrl('logo/mb_logo.png')}
              alt='Logo Light'
              width={200}
              height={200}
              unoptimized
            />
          </Link>
          <CloseButton
            onClick={() => setIsShowMenu(false)}
            className='bg-primary-100 px-3'
            variant='ghost'
          />
        </div>
        <nav className='p-5 flex-1 overflow-y-auto'>
          <ul className='flex flex-col gap-5'>
            <li>
              <Link
                className='relative text-2xl font-medium'
                href={PATH.HOME}
                onClick={() => setIsShowMenu(false)}
              >
                Home
              </Link>
            </li>
            <li>
              <div>
                <Button
                  variant='ghost'
                  className='relative text-2xl font-medium w-full text-left justify-start h-auto p-0'
                  onClick={() => setExpandedMenu(expandedMenu === -1 ? null : -1)}
                >
                  Products
                </Button>
                {expandedMenu === -1 && (
                  <div className='mt-3 ml-4 flex flex-col gap-4'>
                    {MOBILE_PRODUCT_GROUPS.map((group) => (
                      <div key={group.label}>
                        <p className='text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2'>
                          {group.label}
                        </p>
                        <ul className='flex flex-col gap-3'>
                          {group.items.map((product) => (
                            <li key={product.href}>
                              <Link
                                href={product.href}
                                className='text-lg text-gray-700 hover:text-primary'
                                onClick={() => setIsShowMenu(false)}
                              >
                                {product.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </li>
            {navMenusAfterProducts.map((item, index) => {
              const isActive = pathname === item.href;

              return (
                <li key={index}>
                  <Link
                    className='relative text-2xl font-medium'
                    href={item.href}
                    onClick={(e) => {
                      if (item.href.startsWith('/#') && pathname === PATH.HOME) {
                        e.preventDefault();
                        setIsShowMenu(false);
                        const targetId = item.href.substring(2);
                        document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
                        return;
                      }
                      setIsShowMenu(false);
                    }}
                  >
                    {item.title}
                    {isActive && (
                      <span className='inline-block absolute -bottom-3 right-0 left-0 h-1 w-full bg-green-600 rounded-[20px]' />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className='flex flex-col gap-3 items-center p-5 justify-center pb-25'>
          {isAuthenticated ? (
            <Link className='text-blue-600 font-medium' href={PATH.DASHBOARD}>
              Go to app
            </Link>
          ) : (
            <>
              <BookDemoButton
                variant='outline'
                label='Book a call'
                className='w-full'
                onClick={() => {
                  posthog?.capture('nav_book_demo_clicked', { source: 'mobile_menu' });
                  setIsShowMenu(false);
                }}
              />
              <Link
                href={PATH.LOGIN}
                className='py-2 text-sm font-medium text-slate-700 hover:text-[#0848E7]'
                onClick={() => {
                  posthog?.capture('nav_login_clicked');
                  setIsShowMenu(false);
                }}
              >
                Already a customer? Login
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
